import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import Textbox from 'carbon-react/lib/components/textbox';
import Textarea from 'carbon-react/lib/components/textarea';
import { MultiSelect, Option } from 'carbon-react/lib/components/select';

import { WorkspaceMember } from '@futrli-sdk/people';

interface EmailFieldsProps {
  members: WorkspaceMember[];
  selectedUsers: WorkspaceMember[];
  setSelectedUsers: (users: WorkspaceMember[]) => void;
  emailSubject: string;
  setEmailSubject: (value: string) => void;
  emailBody: string;
  setEmailBody: (value: string) => void;
  validationErrors: Record<string, { id: string; message: string }>;
  revalidate: () => void;
  fieldIds: { SEND_TO: string; SUBJECT: string; BODY: string };
  placeholder?: string;
  inputHint?: string;
}

export const EmailFields = ({
  members,
  selectedUsers,
  setSelectedUsers,
  emailSubject,
  setEmailSubject,
  emailBody,
  setEmailBody,
  validationErrors,
  revalidate,
  fieldIds,
  placeholder,
  inputHint
}: EmailFieldsProps) => {
  const intl = useIntl();

  const memberOptions = useMemo(
    () =>
      (members || []).map(m => ({
        ...m,
        text: `${m.name.first_name} ${m.name.last_name} <${m.email_address}>`
      })),
    [members]
  );

  const handleMultiSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValues = ((event.target as unknown) as { value: WorkspaceMember[] }).value;
    setSelectedUsers(selectedValues);
    revalidate();
  };

  return (
    <>
      <MultiSelect
        id={fieldIds.SEND_TO}
        name={fieldIds.SEND_TO}
        label={intl.formatMessage({ id: 'predict.send_to', defaultMessage: 'Send to' })}
        inputHint={inputHint || intl.formatMessage({
          id: 'predict.email_pdf_send_to_hint',
          defaultMessage: 'Select one or more. Type to search if needed.'
        })}
        placeholder={placeholder}
        value={(selectedUsers as unknown) as string[]}
        onChange={handleMultiSelectChange}
        onBlur={revalidate}
        error={validationErrors[fieldIds.SEND_TO]?.message}
        mb={2}
        required
      >
        {memberOptions.map(member => (
          <Option key={member.id} text={member.text} value={(member as unknown) as string} />
        ))}
      </MultiSelect>

      <Textbox
        label={intl.formatMessage({ id: 'predict.email_pdf_subject_label', defaultMessage: 'Email subject' })}
        name={fieldIds.SUBJECT}
        id={fieldIds.SUBJECT}
        value={emailSubject}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailSubject(e.target.value)}
        onBlur={revalidate}
        error={validationErrors[fieldIds.SUBJECT]?.message}
        required
        mb={2}
      />

      <Textarea
        id={fieldIds.BODY}
        label={intl.formatMessage({ id: 'predict.email_pdf_body_label', defaultMessage: 'Email body' })}
        value={emailBody}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEmailBody(e.target.value)}
        onBlur={revalidate}
        required
        rows={8}
        error={validationErrors[fieldIds.BODY]?.message}
        expandable
      />
    </>
  );
};
